import { ExpandMore, OpenInNew } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
	DialogTitle, Typography } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import { FinancialService } from "../../../services/FinancialService";
import { FinancialBillDTO, FinancialDetailDTO, FinancialReviewRowDTO, FinancialServiceDTO } from "../../../types/FinancialDTO";
import { diffItems, diffServices, ItemSide, ServiceChange, ServiceDiff } from "../../../utils/financialDiff";
import { formatCurrency, formatDate } from "../../../utils/financialFunctions";
import "./BillDetailModal.scss";

interface Props {
	row: FinancialReviewRowDTO | null;
	onClose: () => void;
}

interface Side {
	bill?: FinancialBillDTO;
	detail?: FinancialDetailDTO;
	error?: string;
}

interface Nominal {
	previous?: FinancialDetailDTO;
	current?: FinancialDetailDTO;
	error?: string;
}

const GREEN = 'rgba(76, 175, 80, 0.25)';
const RED = 'rgba(244, 67, 54, 0.25)';

const serviceColor = (change: ServiceChange) => {
	if (change === 'NEW' || change === 'UP') {
		return GREEN;
	}
	return change === 'DOWN' || change === 'GONE' ? RED : undefined;
};

const itemColor = (side: ItemSide) => side === 'ADDED' ? GREEN : side === 'REMOVED' ? RED : undefined;

const errorText = (error: any) => typeof error?.response?.data === 'string' ? error.response.data : 'Não foi possível carregar o detalhamento.';

const BillDetailModal: FC<Props> = ({ row, onClose }) => {
	const [previous, setPrevious] = useState<Side>({});
	const [current, setCurrent] = useState<Side>({});
	const [loading, setLoading] = useState(false);
	const [nominal, setNominal] = useState<Nominal | null>(null);
	const [nominalLoading, setNominalLoading] = useState(false);
	const openRow = useRef<FinancialReviewRowDTO | null>(null);
	const service = new FinancialService();

	useEffect(() => {
		if (!row) {
			return;
		}
		const load = async (bill?: FinancialBillDTO): Promise<Side> => {
			if (!bill) {
				return {};
			}
			try {
				const response = await service.getDetail(bill.idConta);
				return { bill, detail: response.data };
			} catch (error) {
				return { bill, error: errorText(error) };
			}
		};
		openRow.current = row;
		let cancelled = false;
		setNominal(null);
		setNominalLoading(false);
		const loadAll = async () => {
			setLoading(true);
			const [before, after] = await Promise.all([load(row.previous), load(row.current)]);
			if (cancelled) {
				return;
			}
			setPrevious(before);
			setCurrent(after);
			setLoading(false);
		};
		loadAll();
		return () => {
			cancelled = true;
		};
	}, [row]);

	const diffs = diffServices(previous.detail?.services, current.detail?.services);

	const loadNominal = async () => {
		if (nominal || nominalLoading || !row) {
			return;
		}
		const requested = row;
		setNominalLoading(true);
		const fetch = async (side: Side) => side.detail?.monthly && side.bill
			? (await service.getNominal(side.bill.idConta)).data
			: undefined;
		try {
			const [before, after] = await Promise.all([fetch(previous), fetch(current)]);
			if (requested === openRow.current) {
				setNominal({ previous: before, current: after });
			}
		} catch (error) {
			if (requested === openRow.current) {
				setNominal({ error: errorText(error) });
			}
		}
		if (requested === openRow.current) {
			setNominalLoading(false);
		}
	};

	const nominalService = (isCurrent: boolean, name: string) =>
		(isCurrent ? nominal?.current : nominal?.previous)?.services.find(item => item.service === name);

	const seal = (item?: FinancialServiceDTO, monthly?: boolean) => {
		if (!item || !monthly) {
			return null;
		}
		return item.matches
			? <Chip size="small" color="success" variant="outlined" label="Confere com a fatura"/>
			: <Chip size="small" color="warning" variant="outlined"
				label={`Aproximado: diferença de ${formatCurrency(Math.abs((item.nominalValue || 0) - (item.value || 0)))}`}/>;
	};

	const column = (side: Side, isCurrent: boolean) => {
		if (!side.bill) {
			return <div className="detail-column"><Typography color="text.secondary">Sem fatura neste mês.</Typography></div>;
		}
		return (
			<div className="detail-column">
				<div className="detail-header">
					<div>
						<Typography variant="h6">{`Emissão ${formatDate(side.bill.issueDate)}`}</Typography>
						<Typography variant="body2" color="text.secondary">
							{`Fatura ${side.bill.idConta} · ${formatCurrency(side.detail?.total ?? side.bill.value)} · ${side.bill.statusText}`}
						</Typography>
					</div>
					<Button variant="contained" color="secondary" endIcon={<OpenInNew/>}
						onClick={() => window.open(side.detail?.sggUrl
							|| `https://app.sgg.net.br/index.php?func=escrituracao_financeira&metodo=comprovanteFaturamento&id=${side.bill?.idConta}`,
							'_blank', 'noopener')}>
						Ver faturamento
					</Button>
				</div>
				{side.error && <Alert severity="error">{side.error}</Alert>}
				{side.detail && !side.detail.monthly &&
					<Alert severity="info">Fatura avulsa: detalhamento nominal indisponível.</Alert>
				}
				{side.detail && diffs.map((diff: ServiceDiff) => {
					const item = isCurrent ? diff.current : diff.previous;
					if (!item && !(isCurrent && diff.change === 'GONE')) {
						return null;
					}
					const own = nominalService(isCurrent, diff.service);
					const other = nominalService(!isCurrent, diff.service);
					const items = diffItems(isCurrent ? other?.items : own?.items, isCurrent ? own?.items : other?.items);
					const marked = isCurrent ? items.current : items.previous;
					return (
						<Accordion key={diff.service} disableGutters onChange={(_, expanded) => expanded && loadNominal()}
							sx={{ backgroundColor: isCurrent ? serviceColor(diff.change) : undefined }}>
							<AccordionSummary expandIcon={<ExpandMore/>}>
								<div className="detail-service">
									<span className="detail-service-name">{diff.service}</span>
									<span>{`${item?.quantity ?? 0} × · ${formatCurrency(item?.value ?? 0)}`}</span>
									{seal(own, side.detail?.monthly)}
								</div>
							</AccordionSummary>
							<AccordionDetails>
								{nominalLoading && <div className="detail-nominal-loading"><CircularProgress size={18}/><span>Carregando lista nominal...</span></div>}
								{nominal?.error && <Alert severity="error">{nominal.error}</Alert>}
								{nominal && !nominal.error && side.detail?.monthly && !marked.length &&
									<Typography variant="body2" color="text.secondary">Sem itens nominais.</Typography>
								}
								{marked.map(({ item: nominal, side: mark }, index) => (
									<div key={`${nominal.employeeId}-${nominal.date}-${index}`} className="detail-item"
										style={{ backgroundColor: itemColor(mark) }}>
										<span>{nominal.employeeName}</span>
										<span>{nominal.date ? formatDate(nominal.date) : ''}</span>
										<span>{nominal.value === undefined || nominal.value === null ? 'sem preço no contrato' : formatCurrency(nominal.value)}</span>
									</div>
								))}
							</AccordionDetails>
						</Accordion>
					);
				})}
			</div>
		);
	};

	return (
		<Dialog open={!!row} onClose={onClose} maxWidth="xl" fullWidth>
			<DialogTitle>{row ? `Detalhamento · ${row.companyName}` : ''}</DialogTitle>
			<DialogContent className="bill-detail-modal">
				{loading
					? <div className="detail-loading"><CircularProgress/><Typography>Carregando faturas...</Typography></div>
					: <div className="detail-columns">
						{column(previous, false)}
						{column(current, true)}
					</div>
				}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Fechar</Button>
			</DialogActions>
		</Dialog>
	);
}

export default BillDetailModal
